import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import NewsCard from './NewsCard';

const CATEGORIES = ['all', 'products', 'events', 'awards', 'industry', 'press'];
const CATEGORY_LABELS = {
  en: { all: 'All', products: 'Products', events: 'Events', awards: 'Awards', industry: 'Industry', press: 'Press' },
  ar: { all: 'الكل', products: 'المنتجات', events: 'الفعاليات', awards: 'الجوائز', industry: 'الصناعة', press: 'صحافة' },
};

const SkeletonCard = ({ large }) => (
  <div className={`rounded-sm skeleton ${large ? 'min-h-[420px]' : 'min-h-[260px]'}`} />
);

const NewsSection = ({ compact = false }) => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const isRtl = language === 'ar';
  const [articles, setArticles] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  const fetchNews = useCallback(async (cat, pg, append = false) => {
    if (pg === 1) setLoading(true); else setLoadingMore(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: compact ? 3 : 9 });
      if (cat !== 'all') params.set('category', cat);
      const r = await fetch(`/api/news?${params}`);
      const data = await r.json();
      if (append) {
        setArticles(prev => [...prev, ...(data.articles || [])]);
      } else {
        setArticles(data.articles || []);
      }
      setTotalPages(data.pages || 1);
      if (data.categories) setCategoryCounts(data.categories);
    } catch {
      if (!append) setArticles([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [compact]);

  useEffect(() => {
    setPage(1);
    fetchNews(activeCategory, 1, false);
  }, [activeCategory, fetchNews]);

  // Intersection Observer for staggered entrance animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNews(activeCategory, nextPage, true);
  };

  // First article as featured, rest as cards
  const featured = articles[0] || null;
  const rest = articles.slice(1);

  return (
    <section id="news" className={`${isDark ? 'bg-[#0D0D0D]' : 'bg-[#F5F0E8]'}`}>
      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">

        {/* Section Header */}
        <div className={`flex flex-col sm:items-end sm:justify-between gap-6 mb-10 ${isRtl ? 'sm:flex-row-reverse' : 'sm:flex-row'}`}>
          <div className={isRtl ? 'text-right' : 'text-left'}>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] uppercase mb-2">
              ◆ {language === 'ar' ? 'النشرة الملكية' : 'ROYAL DISPATCH'}
            </p>
            <h2 className={`font-display text-3xl sm:text-4xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {language === 'ar' ? 'آخر الأخبار' : 'Latest News'}
            </h2>
            <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
              {language === 'ar'
                ? 'إطلاقات، مناسبات، وإنجازات من عالم رويال'
                : 'Launches, milestones, and stories from Royal Tobacco'}
            </p>
          </div>

          {/* Category Tabs — hidden in compact/homepage mode */}
          {!compact && (
            <div className={`flex flex-wrap gap-2 ${isRtl ? 'justify-end' : ''}`}>
              {CATEGORIES.map(cat => {
                const count = cat === 'all'
                  ? Object.values(categoryCounts).reduce((a, b) => a + b, 0)
                  : (categoryCounts[cat] || 0);
                const active = cat === activeCategory;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full transition-all duration-200 ${
                      active
                        ? 'bg-[#D4AF37] text-black'
                        : isDark
                          ? 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
                          : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800 border border-gray-200'
                    }`}
                  >
                    {CATEGORY_LABELS[language][cat]}
                    {count > 0 && (
                      <span className={`${isRtl ? 'mr-1.5' : 'ml-1.5'} text-[10px] ${active ? 'opacity-70' : 'opacity-50'}`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="sm:col-span-2 lg:col-span-2 lg:row-span-2"><SkeletonCard large /></div>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : articles.length === 0 ? (
          <div className={`flex flex-col items-center justify-center py-20 rounded-sm border ${
            isDark ? 'border-[rgba(212,175,55,0.1)] text-white/30' : 'border-gray-200 text-gray-400'
          }`}>
            <p className="text-4xl mb-4">◆</p>
            <p className="font-display text-lg font-semibold mb-1">
              {language === 'ar' ? 'لا توجد مقالات' : 'No Articles Yet'}
            </p>
            <p className="text-sm">
              {language === 'ar' ? 'لا توجد أخبار في هذه الفئة حالياً' : 'No news in this category at the moment'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Featured card — spans 2 cols & 2 rows on desktop */}
              {featured && (
                <div
                  className={`sm:col-span-2 lg:col-span-2 lg:row-span-2 transition-all duration-700 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: '0ms' }}
                >
                  <NewsCard article={{ ...featured, featured: true }} featured />
                </div>
              )}

              {/* Supporting cards */}
              {rest.map((article, i) => (
                <div
                  key={article.id || article.slug}
                  className={`transition-all duration-700 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${(i + 1) * 100}ms` }}
                >
                  <NewsCard article={article} />
                </div>
              ))}
            </div>

            {/* Load More — full page only */}
            {!compact && page < totalPages && (
              <div className="flex justify-center mt-10">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-8 py-3 border border-[rgba(212,175,55,0.4)] text-[#D4AF37] text-sm font-semibold uppercase tracking-widest rounded-sm hover:bg-[rgba(212,175,55,0.1)] disabled:opacity-50 transition-all duration-300"
                >
                  {loadingMore
                    ? (language === 'ar' ? 'جارٍ التحميل...' : 'Loading...')
                    : (language === 'ar' ? 'تحميل المزيد' : 'Load More')}
                </button>
              </div>
            )}

            {/* View All News — homepage compact mode only */}
            {compact && (
              <div className="flex justify-center mt-10">
                <Link
                  to="/news"
                  className="px-8 py-3 border border-[rgba(212,175,55,0.4)] text-[#D4AF37] text-sm font-semibold uppercase tracking-widest rounded-sm hover:bg-[rgba(212,175,55,0.1)] transition-all duration-300"
                >
                  {language === 'ar' ? '← عرض كل الأخبار' : 'View All News →'}
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default NewsSection;
